// components/ImportFromOrderModal.js
// Modal for importing prizes and/or vehicles from another order
import React, { useState, useMemo } from 'react';
import { useAppData } from '../context/AppDataContext';

export default function ImportFromOrderModal({
  isOpen,
  onClose,
  mode, // 'prizes' or 'vehicles'
  currentOrderId,
  onImport,
}) {
  const { allOrders } = useAppData();
  const [search, setSearch] = useState('');
  const [selectedOrderId, setSelectedOrderId] = useState('');

  const filteredOrders = useMemo(() => {
    const q = search.toLowerCase().trim();
    return allOrders
      .filter(o => o.id !== currentOrderId)
      .filter(o => {
        if (!q) return true;
        return (
          String(o.jobNumber || '').toLowerCase().includes(q) ||
          (o.campaignName || '').toLowerCase().includes(q) ||
          (o.dealershipName || '').toLowerCase().includes(q)
        );
      })
      .slice(0, 50);
  }, [allOrders, currentOrderId, search]);

  const selectedOrder = useMemo(
    () => allOrders.find(o => o.id === selectedOrderId) || null,
    [allOrders, selectedOrderId]
  );

  const previewItems = useMemo(() => {
    if (!selectedOrder) return [];
    if (mode === 'prizes') {
      return (selectedOrder.prizes || []).filter(p => p.prize);
    }
    if (mode === 'vehicles') {
      return (selectedOrder.vehicles || []).filter(v => v.year || v.make || v.model);
    }
    return [];
  }, [selectedOrder, mode]);

  const handleImport = () => {
    if (!selectedOrder) return;
    if (mode === 'prizes') {
      onImport('prizes', {
        prizes: selectedOrder.prizes || [],
        totalOdds: selectedOrder.totalOdds || 50000,
      });
    } else {
      onImport('vehicles', {
        vehicles: selectedOrder.vehicles || [],
      });
    }
    handleClose();
  };

  const handleClose = () => {
    setSearch('');
    setSelectedOrderId('');
    onClose();
  };

  if (!isOpen) return null;

  const isPrizes = mode === 'prizes';
  const title = isPrizes ? 'Import Prizes' : 'Import Vehicles';
  const accentColor = isPrizes ? '#8b5cf6' : '#10b981';
  const warningText = isPrizes
    ? '⚠️ This will overwrite the current prizes in this order.'
    : 'Imported vehicles will be appended to the existing vehicles in this order.';

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px',
      }}
      onClick={e => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div style={{
        background: 'white',
        borderRadius: '12px',
        width: '100%',
        maxWidth: '560px',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          background: `linear-gradient(135deg, ${accentColor}, ${isPrizes ? '#6d28d9' : '#059669'})`,
          padding: '20px 24px',
          color: 'white',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '700' }}>{title}</h2>
            <p style={{ margin: '4px 0 0', fontSize: '13px', opacity: 0.85 }}>
              {isPrizes ? 'Select an order to import prizes from' : 'Select an order to import vehicles from'}
            </p>
          </div>
          <button
            onClick={handleClose}
            style={{
              background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '50%',
              width: '32px', height: '32px', color: 'white', fontSize: '18px',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              lineHeight: 1,
            }}
          >×</button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>
          {/* Search */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
              Search Orders
            </label>
            <input
              type="text"
              placeholder="Search by job #, campaign, or dealership..."
              value={search}
              onChange={e => { setSearch(e.target.value); setSelectedOrderId(''); }}
              autoFocus
              style={{
                width: '100%', padding: '10px 12px',
                border: '1.5px solid #d1d5db', borderRadius: '8px',
                fontSize: '14px', boxSizing: 'border-box',
                outline: 'none',
              }}
            />
          </div>

          {/* Order List */}
          {!selectedOrderId && (
            <div style={{
              border: '1px solid #e5e7eb', borderRadius: '8px',
              overflow: 'hidden', marginBottom: '16px', maxHeight: '260px', overflowY: 'auto',
            }}>
              {filteredOrders.length === 0 ? (
                <div style={{ padding: '24px', textAlign: 'center', color: '#9ca3af', fontSize: '14px' }}>
                  {search ? 'No orders found matching your search.' : 'No other orders available.'}
                </div>
              ) : (
                filteredOrders.map(order => (
                  <div
                    key={order.id}
                    onClick={() => { setSelectedOrderId(order.id); }}
                    style={{
                      padding: '12px 16px', cursor: 'pointer',
                      borderBottom: '1px solid #f3f4f6',
                      display: 'flex', flexDirection: 'column', gap: '2px',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                    onMouseLeave={e => e.currentTarget.style.background = 'white'}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: '700', fontSize: '14px', color: '#1f2937' }}>
                        #{order.jobNumber}
                      </span>
                      <span style={{ fontSize: '11px', color: '#9ca3af' }}>{order.status}</span>
                    </div>
                    {order.campaignName && (
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>{order.campaignName}</span>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {/* Selected Order Preview */}
          {selectedOrder && (
            <div style={{
              border: `2px solid ${accentColor}`,
              borderRadius: '8px', overflow: 'hidden',
              marginBottom: '16px',
            }}>
              {/* Selected Order Header */}
              <div style={{
                background: `${accentColor}15`, padding: '10px 16px',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                borderBottom: `1px solid ${accentColor}30`,
              }}>
                <div>
                  <span style={{ fontWeight: '700', fontSize: '15px', color: '#1f2937' }}>
                    #{selectedOrder.jobNumber}
                  </span>
                  {selectedOrder.campaignName && (
                    <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: '8px' }}>
                      {selectedOrder.campaignName}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => { setSelectedOrderId(''); }}
                  style={{
                    background: 'none', border: 'none', color: '#6b7280',
                    fontSize: '12px', cursor: 'pointer', textDecoration: 'underline',
                  }}
                >
                  Change
                </button>
              </div>

              {/* Preview */}
              <div style={{ padding: '12px 16px' }}>
                <p style={{ margin: '0 0 8px', fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                  {isPrizes ? 'Prizes in this order:' : 'Vehicles in this order:'}
                </p>
                {previewItems.length === 0 ? (
                  <p style={{ margin: 0, fontSize: '13px', color: '#9ca3af', fontStyle: 'italic' }}>
                    No {isPrizes ? 'prizes' : 'vehicles'} found in this order.
                  </p>
                ) : (
                  <ul style={{ margin: 0, padding: '0 0 0 18px' }}>
                    {previewItems.map((item, i) => (
                      <li key={i} style={{ fontSize: '13px', color: '#374151', marginBottom: '3px' }}>
                        {isPrizes
                          ? `${item.prize === 'Other' ? item.custom || 'Custom Prize' : item.prize}${item.odds ? ` - ${item.odds}` : ''}${item.value ? ` (${item.value})` : ''}`
                          : `${item.year || ''} ${item.make || ''} ${item.model || ''}`.trim() || 'Vehicle'
                        }
                      </li>
                    ))}
                  </ul>
                )}
                {isPrizes && selectedOrder.totalOdds && (
                  <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#6b7280' }}>
                    Total Odds: {selectedOrder.totalOdds.toLocaleString()}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Warning */}
          {selectedOrder && previewItems.length > 0 && (
            <div style={{
              padding: '10px 14px',
              background: isPrizes ? '#fef3c7' : '#d1fae5',
              border: `1px solid ${isPrizes ? '#f59e0b' : '#6ee7b7'}`,
              borderRadius: '8px',
              fontSize: '13px',
              color: isPrizes ? '#92400e' : '#065f46',
              marginBottom: '4px',
            }}>
              {warningText}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #e5e7eb',
          display: 'flex', justifyContent: 'flex-end', gap: '10px',
          background: '#f9fafb',
        }}>
          <button
            onClick={handleClose}
            style={{
              padding: '9px 20px', background: 'white',
              border: '1.5px solid #d1d5db', borderRadius: '8px',
              fontSize: '14px', fontWeight: '500', cursor: 'pointer', color: '#374151',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleImport}
            disabled={!selectedOrder || previewItems.length === 0}
            style={{
              padding: '9px 20px',
              background: !selectedOrder || previewItems.length === 0 ? '#d1d5db' : accentColor,
              color: 'white', border: 'none', borderRadius: '8px',
              fontSize: '14px', fontWeight: '600',
              cursor: !selectedOrder || previewItems.length === 0 ? 'not-allowed' : 'pointer',
              transition: 'background 0.15s',
            }}
          >
            {isPrizes ? `Import ${previewItems.length} Prize${previewItems.length !== 1 ? 's' : ''}` : `Import ${previewItems.length} Vehicle${previewItems.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}